import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

export const SEVERITIES = ['CRITICAL', 'WARNING', 'INFO'] as const;
export const ALERT_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'RESOLVED'] as const;

export type Severity = (typeof SEVERITIES)[number];
export type AlertStatus = (typeof ALERT_STATUSES)[number];

/**
 * WIERSZ LISTY ALERTÓW = GRUPA „ten sam fakt", nie jeden zapis w bazie.
 *
 * ⚠️⚠️ ZMIENIONE 31.08.2026 po zgłoszeniu „w zakładce alerty jest straszny pierdolnik przez
 * zdublowane alerty z tej samej godziny". Serwer zwija wiersze o tej samej treści, kodzie i stanie
 * w jeden, z licznikiem — dlatego nie ma tu `id` (grupa to n wierszy), a jest `kluczGrupy`,
 * `licznik` i `pierwszy`.
 */
const AlertSchema = z.object({
  kluczGrupy: z.string(),
  severity: z.enum(SEVERITIES),
  code: z.string(),
  message: z.string(),
  status: z.enum(ALERT_STATUSES),
  /** Najnowsze wystąpienie w grupie. */
  createdAt: z.string(),
  /** Najstarsze wystąpienie — „to trwa od…". */
  pierwszy: z.string(),
  /** Ile wierszy w bazie stoi za tym wierszem. `1` = zwykły alert. */
  licznik: z.number(),
});

export type Alert = z.infer<typeof AlertSchema>;

const ListSchema = z.object({
  rows: z.array(AlertSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

export type AlertsQuery = {
  groupId: string;
  severity?: Severity;
  status?: AlertStatus;
  code?: string;
  search?: string;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
};

export const fetchAlerts = (q: AlertsQuery) => {
  const p = new URLSearchParams({
    groupId: q.groupId,
    page: String(q.page),
    pageSize: String(q.pageSize),
    sort: q.sort,
    dir: q.dir,
  });
  if (q.severity) p.set('severity', q.severity);
  if (q.status) p.set('status', q.status);
  if (q.code) p.set('code', q.code);
  if (q.search) p.set('search', q.search);
  return apiGet(`/alerts?${p.toString()}`, ListSchema);
};

const SummarySchema = z.object({
  openBySeverity: z.record(z.string(), z.number()),
  byStatus: z.record(z.string(), z.number()),
  codes: z.array(z.object({ code: z.string(), count: z.number() })),
  oldestOpen: z.object({ id: z.string(), code: z.string(), message: z.string(), createdAt: z.string() }).nullable(),
  newestAt: z.string().nullable(),
});

export type AlertsSummary = z.infer<typeof SummarySchema>;

export const fetchAlertsSummary = (groupId: string) => apiGet(`/alerts/summary?groupId=${groupId}`, SummarySchema);

const DetectedSchema = z.object({
  thresholds: z.object({ silentMinutes: z.number(), lowStockAt: z.number(), clockSkewSeconds: z.number() }),
  /**
   * Automaty, których agent NIE ODPOWIADA. Maszyn bez agenta tu NIE MA — one nie milczą,
   * tylko nie są monitorowane (patrz `unmonitoredMachines`). Wcześniej wpadały tutaj i alert
   * wzywał do naprawy łączności tam, gdzie nic nigdy nie działało.
   */
  silentMachines: z.object({
    count: z.number(),
    /** mianownik to automaty Z AGENTEM, nie cała flota — inaczej mieszał dwie populacje */
    outOf: z.number(),
    rows: z.array(
      z.object({
        id: z.string(),
        vmid: z.number(),
        name: z.string(),
        online: z.boolean(),
        lastSeenAt: z.string().nullable(),
        presence: z.enum(['SILENT', 'NEVER_CONNECTED']),
        hoursSilent: z.number().nullable(),
        contradiction: z.boolean(),
      }),
    ),
  }),
  /** LUKA WDROŻENIOWA, nie awaria — dlatego osobna pozycja, nie doliczana do milczących. */
  unmonitoredMachines: z.object({
    count: z.number(),
    outOf: z.number(),
    rows: z.array(
      z.object({ id: z.string(), vmid: z.number(), name: z.string(), reportedOnline: z.boolean() }),
    ),
  }),
  /**
   * ROZJECHANY ZEGAR AUTOMATU — awaria BEZ OBJAWU przy maszynie. Wydania niosą godzinę z zegara
   * automatu, więc rozjazd wpycha je do złego okresu rozliczeniowego i przesuwa okno limitów,
   * a przy samym automacie wszystko wygląda normalnie.
   */
  clockSkew: z.object({
    count: z.number(),
    /** Mianownik to automaty ZE ZMIERZONYM zegarem — bez pomiaru nie ma czego porównywać. */
    outOf: z.number(),
    rows: z.array(
      z.object({
        id: z.string(),
        vmid: z.number(),
        name: z.string(),
        /** Ze ZNAKIEM: dodatni = zegar maszyny spieszy się względem portalu. */
        skewSeconds: z.number(),
        minutes: z.number(),
        machineTime: z.string().nullable(),
      }),
    ),
  }),
  lowStock: z.object({
    count: z.number(),
    outOf: z.number(),
    truncated: z.boolean(),
    rows: z.array(
      z.object({
        id: z.string(),
        vmid: z.number(),
        machine: z.string(),
        drawerNo: z.number(),
        compartmentNo: z.number(),
        item: z.string(),
        unit: z.string(),
        qtyCurrent: z.number(),
        qtyMax: z.number(),
      }),
    ),
  }),
  rationing: z.object({ overPeople: z.number(), blindPeople: z.number(), rulesWithBlind: z.number() }),
  computedNow: z.boolean(),
});

export type Detected = z.infer<typeof DetectedSchema>;

/**
 * Progi są OPCJONALNE — pominięte znaczy „weź domyślne serwera", a nie „zero".
 * Pulpit nie ma suwaków i nie powinien ich udawać; ekran Alerty podaje swoje.
 */
export const fetchDetected = (groupId: string, silentMinutes?: number, lowStockAt?: number) => {
  const q = new URLSearchParams({ groupId });
  if (silentMinutes !== undefined) q.set('silentMinutes', String(silentMinutes));
  if (lowStockAt !== undefined) q.set('lowStockAt', String(lowStockAt));
  return apiGet(`/alerts/detected?${q.toString()}`, DetectedSchema);
};

/**
 * POTWIERDZENIE CAŁEJ GRUPY — po kodzie i treści, nie po `id`.
 *
 * ⚠️ Wiersz na ekranie to grupa n wierszy w bazie, więc potwierdzanie po identyfikatorze
 * zostawiałoby resztę otwartą: człowiek klikałby osiemnaście razy w jeden fakt.
 */
export const acknowledgeAlert = (groupId: string, code: string, message: string) =>
  apiSend('POST', '/alerts/ack', { groupId, code, message }, z.object({ potwierdzono: z.number() }));

/** Ile godzin temu — do pokazania „kanał zgłoszeń milczy od…". */
export function hoursAgo(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
}
